import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const pad = (n) => String(n).padStart(2, "0");

export default function AvailabilityCalendar({ periods = [], blocked = [], halfDay = false, selected, onSelect }) {
  const now = new Date();
  const todayIso = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const { year, month } = cursor;

  const firstOffset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);

  const atCurrentMonth = year === now.getFullYear() && month === now.getMonth();
  const move = (dir) =>
    setCursor(({ year: y, month: m }) => {
      const nm = m + dir;
      return { year: y + Math.floor(nm / 12), month: ((nm % 12) + 12) % 12 };
    });

  function dayState(iso) {
    if (iso < todayIso) return "past";
    if (periods.length > 0 && !periods.some((p) => p.start <= iso && iso <= p.end)) return "season";
    const slots = new Set(blocked.filter((e) => e.date === iso).map((e) => e.slot));
    if (slots.has("full")) return "off";
    if (!halfDay) return slots.size > 0 ? "off" : "free";
    if (slots.has("am") && slots.has("pm")) return "off";
    if (slots.has("am") || slots.has("pm")) return "half";
    return "free";
  }

  return (
    <div className="rounded-2xl border border-ink/15 bg-white p-4 sm:p-5" data-testid="availability-calendar">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => move(-1)} disabled={atCurrentMonth} data-testid="avail-prev" aria-label="Previous month"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/15 text-ink/60 transition-colors duration-300 enabled:hover:border-terra enabled:hover:text-terra disabled:opacity-30">
          <ChevronLeft size={16} />
        </button>
        <p className="font-serif text-xl tracking-tight" data-testid="avail-month">
          {MONTHS[month]} <span className="text-ink/40">{year}</span>
        </p>
        <button type="button" onClick={() => move(1)} data-testid="avail-next" aria-label="Next month"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/15 text-ink/60 transition-colors duration-300 hover:border-terra hover:text-terra">
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-7 gap-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="pb-1 text-center text-sm uppercase tracking-[0.15em] text-ink/40">{w}</div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const iso = `${year}-${pad(month + 1)}-${pad(day)}`;
          const st = dayState(iso);
          const disabled = st === "past" || st === "season" || st === "off";
          const isSel = selected === iso;
          return (
            <button
              type="button"
              key={iso}
              disabled={disabled}
              onClick={() => onSelect(iso)}
              data-testid={`avail-day-${iso}`}
              data-state={st}
              className={`relative flex h-10 items-center justify-center rounded-lg text-base transition-colors duration-200 sm:h-11 ${
                isSel
                  ? "bg-terra font-semibold text-paper"
                  : st === "off"
                    ? "bg-red-50 font-medium text-red-400 line-through decoration-red-400/70"
                    : st === "free" || st === "half"
                      ? "bg-olive-pale/70 text-ink hover:bg-terra hover:text-paper"
                      : "text-ink/30 [background-image:repeating-linear-gradient(135deg,transparent,transparent_5px,rgba(28,25,23,0.06)_5px,rgba(28,25,23,0.06)_7px)]"
              }`}
            >
              {day}
              {st === "half" && !isSel && <span className="absolute bottom-1 h-1 w-1 rounded-full bg-amber-500" />}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-ink/50" data-testid="avail-legend">
        <span className="flex items-center gap-1.5"><span className="h-3.5 w-3.5 rounded-md bg-olive-pale ring-1 ring-olive/30" /> Available</span>
        {halfDay && <span className="flex items-center gap-1.5"><span className="h-3.5 w-3.5 rounded-md bg-amber-400" /> One half booked</span>}
        <span className="flex items-center gap-1.5"><span className="flex h-3.5 w-3.5 items-center justify-center rounded-md bg-red-50 ring-1 ring-red-200"><span className="h-px w-2.5 bg-red-400" /></span> Taken</span>
        {periods.length > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="h-3.5 w-3.5 rounded-md ring-1 ring-ink/10 [background-image:repeating-linear-gradient(135deg,transparent,transparent_2px,rgba(28,25,23,0.15)_2px,rgba(28,25,23,0.15)_3px)]" />
            Not operating
          </span>
        )}
      </div>
    </div>
  );
}
